/**
 * Book → Volunteer help → Events (Eli, 17 Oct 2026): "it shows any events that have been posted already from the
 * Events page. Then I can select that event that I'm requesting volunteers for." Pick the event (its day, time and
 * place come with it) → how many volunteers → email every Ready volunteer. It is the event's own "Request volunteers"
 * press (the number is saved with the event first; the request goes out in parts until everyone is reached); whoever
 * answers past the number is asked the standby question. Nothing about tenants goes to volunteers.
 */
import { useMemo, useState, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import type { BookProgram } from '@shared/book-programs';
import type { EventSendResult, EventView } from '@shared/events';
import { minutesLabel } from '@shared/volunteers';
import { useAnnounce } from '@/components/Announcer';
import { Icon } from '@/components/Icons';
import { StepHead, SummaryCard } from '@/components/Steps';
import { Button } from '@/components/ui/button';
import { SheetContent } from '@/components/ui/sheet';
import { useToast } from '@/components/ui/use-toast';
import { formatDayMedium } from '@/lib/dates';
import { errorText } from '@/features/people/api';
import { TextField } from '@/features/people/fields';
import { ProgramPicker } from '@/features/booking/ProgramPicker';
import { useEvents } from '@/features/events/api';
import { editEvent } from '@/features/events/api177';
import { requestVolunteersReq } from '@/features/events/api177';

type Step = 'event' | 'count' | 'confirm';
const STEPS: Step[] = ['event', 'count', 'confirm'];

export function VolunteerHelpEvents({ onDone, onBack, onProgram, returnFocusTo }: { onDone: () => void; onBack: () => void; onProgram?: (p: BookProgram) => void; returnFocusTo?: RefObject<HTMLElement | null> }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language === 'es' ? 'es' : 'en';
  const announce = useAnnounce();
  const toast = useToast();
  const events = useEvents();
  const [step, setStep] = useState<Step>('event');
  const [eventId, setEventId] = useState<string | null>(null);
  const [count, setCount] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const list = useMemo(() => (events.data?.upcoming ?? []).filter((e) => !e.outing && e.status !== 'Cancelled').sort((a, b) => a.dayKey.localeCompare(b.dayKey) || (a.minutes ?? 0) - (b.minutes ?? 0)), [events.data]);
  const ev: EventView | null = list.find((e) => e.eventId === eventId) ?? null;
  const n = STEPS.indexOf(step) + 1;
  const howMany = Math.max(1, Math.min(200, Number(count) || 0));
  const when = (e: EventView) => `${formatDayMedium(e.dayKey, lang)}${e.minutes !== null ? ` · ${minutesLabel(e.minutes)}` : ''}`;

  const refuse = (m: string) => {
    setProblem(m);
    announce(m, 'assertive');
  };
  const go = (s: Step) => {
    setProblem(null);
    setStep(s);
  };
  const next = () => {
    if (step === 'event') return ev ? go('count') : refuse(t('vb.h.needEvent'));
    if (step === 'count') return Number(count) >= 1 ? go('confirm') : refuse(t('vb.h.needCount'));
  };
  const back = () => (n === 1 ? onBack() : go(STEPS[n - 2]!));
  const choose = (e: EventView) => {
    setEventId(e.eventId);
    setCount(String(e.numbers.volunteerPlaces && e.numbers.volunteerPlaces > 0 ? e.numbers.volunteerPlaces : 2));
    go('count');
  };

  const send = async () => {
    if (!ev) return;
    setBusy(true);
    let queued = 0;
    let last: (EventSendResult & { remaining?: number }) | null = null;
    try {
      await editEvent(ev.eventId, { volunteersNeeded: howMany });
      for (let part = 0; part < 60; part++) {
        last = await requestVolunteersReq(ev.eventId);
        queued += last.queued;
        if (!last.remaining) break;
      }
      const m = last ? (queued !== last.queued ? `${t('cal177e.helpers.sentTotal', { count: queued })} ${last.message}` : last.message) : '';
      toast(m);
      announce(m);
      onDone();
    } catch (e) {
      refuse(`${queued ? `${t('cal177e.helpers.sentTotal', { count: queued })} ` : ''}${errorText(e)}`);
    } finally {
      setBusy(false);
    }
  };

  let body: React.ReactNode = null;
  if (step === 'event') {
    body = events.isLoading ? (
      <p role="status">{t('vb.h.loadingEvents')}</p>
    ) : !list.length ? (
      <p className="help calm" data-testid="vhe-none">
        {t('vb.h.noEvents')}
      </p>
    ) : (
      <div className="cat-cards" role="group" aria-labelledby="vhe-q" data-testid="vhe-events">
        {list.map((e) => (
          <button key={e.eventId} type="button" aria-pressed={eventId === e.eventId} onClick={() => choose(e)} data-testid={`vhe-event-${e.eventId}`}>
            <b>{e.name}</b>
            <small>
              {when(e)}
              {e.place ? ` · ${e.place}` : ''}
            </small>
          </button>
        ))}
      </div>
    );
  } else if (step === 'count') {
    body = (
      <>
        {ev ? (
          <p className="picked" data-testid="vhe-picked">
            <b>{ev.name}</b> · {when(ev)}
          </p>
        ) : null}
        <TextField id="vhe-count" label={t('vb.h.countLabel')} value={count} onChange={(v) => setCount(v.replace(/\D/g, '').slice(0, 3))} inputMode="numeric" required help={t('vb.h.countHelp')} />
      </>
    );
  } else if (ev) {
    body = (
      <SummaryCard
        testId="vhe-confirm"
        rows={[
          { label: t('vb.h.c.event'), value: ev.name },
          { label: t('vol.req.c.when'), value: when(ev) },
          { label: t('vol.req.c.where'), value: ev.place || '—' },
          { label: t('vb.h.c.needed'), value: t('vb.h.needed', { count: howMany }) },
        ]}
      >
        <p className="help">{t('vb.h.sendNote')}</p>
      </SummaryCard>
    );
  }

  return (
    <SheetContent
      title={t('book183.program.volunteer-help')}
      wide
      returnFocusTo={returnFocusTo}
      data-testid="volunteer-help-sheet"
      footer={
        <>
          <Button variant="secondary" onClick={back} data-testid="vhe-back">
            {t('svc183.help.back')}
          </Button>
          {step === 'confirm' ? (
            <Button disabled={busy} onClick={() => send()} data-testid="vhe-send">
              {t('vb.h.emailAll', { count: howMany })}
            </Button>
          ) : step === 'event' && !ev ? null : (
            <Button onClick={next} data-testid="vhe-next">
              {t('svc183.help.next')}
            </Button>
          )}
        </>
      }
    >
      {onProgram ? <ProgramPicker value="volunteer-help" onChange={onProgram} /> : null}
      <StepHead n={n} of={STEPS.length} question={t(`vb.h.q.${step}`)} id="vhe-q" testId="vhe-question" />
      {body}
      {problem ? (
        <p className="field-err" role="alert" data-testid="vhe-problem">
          <Icon name="alert" width={16} height={16} /> {problem}
        </p>
      ) : null}
    </SheetContent>
  );
}
